<template>
  <div class="waste-visualization">
    <!-- Mode saisie (pour agents) -->
    <div v-if="mode === 'input'" class="space-y-4">
      <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
        <h2 class="text-xl font-bold mb-2">♻️ Mesure du Gaspillage</h2>
        <p class="text-sm opacity-90">Pesée après le repas</p>
      </div>

      <!-- Pesée par catégorie -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3">⚖️ Pesées en quantité de barquettes remplis</h3>
        <div class="space-y-3">
          <div
            v-for="cat in wasteCategories"
            :key="cat.id"
            class="border-2 rounded-lg p-3"
            :class="cat.weight > 0 ? 'border-orange-300 bg-orange-50' : 'border-gray-200'"
          >
            <div class="flex items-center justify-between mb-2">
              <span class="font-semibold text-gray-700">{{ cat.icon }} {{ cat.name }}</span>
              <span class="text-2xl font-bold" :class="cat.weight > 0 ? 'text-orange-600' : 'text-gray-400'">
                {{ cat.barquette ? optToKg(cat.name, cat.barquette) + " kg" : "0 kg" }}
              </span>
            </div>
            <div class="flex gap-2 mt-2">
              <button
                v-for="opt in barquetteOptions[cat.name]"
                :key="opt.value"
                @click="setBarquette(cat, opt.value)"
                :class="[
                  'px-2 py-1 rounded border',
                  cat.barquette === opt.value
                    ? 'bg-blue-500 text-white border-blue-600'
                    : 'bg-gray-100 border-gray-300',
                ]"
              >
                {{ opt.label }}
              </button>
              <span class="ml-2 text-xs text-gray-500">barquette(s) jetée(s)</span>
            </div>
          </div>
        </div>

        <div class="mt-4 bg-red-50 rounded-lg p-4 text-center border-2 border-red-300">
          <p class="text-sm text-gray-600 mb-1">Total gaspillé</p>
          <p class="text-4xl font-bold text-red-600">{{ totalWaste }} kg</p>
        </div>
      </div>

      <!-- Photos des poubelles -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3">📸 Photos des poubelles</h3>
        <div class="flex gap-2 flex-wrap">
          <div
            v-for="(photo, idx) in photos"
            :key="idx"
            class="relative w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center"
          >
            <span class="text-3xl">🗑️</span>
            <button
              @click="removePhoto(idx)"
              class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
            >
              ✕
            </button>
          </div>
          <button
            @click="addPhoto"
            class="w-24 h-24 bg-blue-50 border-2 border-blue-300 border-dashed rounded-lg flex items-center justify-center hover:bg-blue-100"
          >
            <span class="text-3xl text-blue-500">📷</span>
          </button>
        </div>
      </div>

      <!-- Boutons d'action -->
      <div class="grid grid-cols-2 gap-3">
        <button
          @click="generateVisualization"
          :disabled="totalWaste === 0"
          :class="totalWaste > 0 ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-300 cursor-not-allowed'"
          class="text-white rounded-lg p-4 font-semibold shadow-md transition"
        >
          👀 Voir le résultat
        </button>
        <button
          @click="saveForTomorrow"
          :disabled="totalWaste === 0"
          :class="totalWaste > 0 ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300 cursor-not-allowed'"
          class="text-white rounded-lg p-4 font-semibold shadow-md transition"
        >
          🖨️ Préparer affiche
        </button>
      </div>
    </div>

    <!-- Mode visualisation ludique (pour enfants) -->
    <div v-if="mode === 'display'" class="space-y-4">
      <div class="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-6 text-white shadow-lg text-center">
        <h2 class="text-2xl font-bold mb-2">🗑️ Gaspillage du jour</h2>
        <p class="text-sm opacity-90">Lundi 5 janvier - Déjeuner</p>
      </div>

      <!-- Poubelle visuelle animée -->
      <div class="bg-white rounded-xl p-6 shadow-sm text-center">
        <div class="relative inline-block">
          <!-- Poubelle SVG -->
          <div class="text-9xl">🗑️</div>
          <!-- Niveau de remplissage -->
          <div
            class="absolute bottom-0 left-0 right-0 bg-red-500 rounded-b-3xl transition-all"
            :style="{ height: wasteLevel + '%', opacity: 0.7 }"
          ></div>
        </div>
        <p class="text-5xl font-bold text-red-600 mt-4">{{ totalWaste }} kg</p>
        <p class="text-lg text-gray-600 mt-2">jetés à la poubelle ! 😢</p>
      </div>

      <!-- Équivalences concrètes -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3 text-center">C'est comme si on jetait...</h3>
        <div class="space-y-3">
          <div
            v-for="equiv in equivalences"
            :key="equiv.id"
            class="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border-2 border-blue-200"
          >
            <div class="flex items-center gap-4">
              <span class="text-5xl">{{ equiv.icon }}</span>
              <div class="flex-1">
                <p class="text-3xl font-bold text-blue-600">{{ equiv.quantity }}</p>
                <p class="text-sm text-gray-700">{{ equiv.label }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Détail par catégorie avec emojis -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3 text-center">📊 Détail du gaspillage</h3>
        <div class="space-y-2">
          <div
            v-for="cat in wasteCategories.filter((c) => c.weight > 0)"
            :key="cat.id"
            class="flex items-center justify-between bg-orange-50 rounded-lg p-3"
          >
            <span class="text-2xl">{{ cat.icon }}</span>
            <span class="font-semibold text-gray-700">{{ cat.name }}</span>
            <span class="text-xl font-bold text-orange-600">{{ cat.weight }} kg</span>
          </div>
        </div>
      </div>

      <!-- Challenge & encouragement -->
      <div class="bg-gradient-to-r from-green-400 to-green-500 rounded-xl p-6 text-white shadow-lg text-center">
        <p class="text-3xl mb-3">🎯</p>
        <p class="text-xl font-bold mb-2">Notre défi</p>
        <p class="text-lg">Essayons de faire mieux demain !</p>
        <p class="text-sm opacity-90 mt-2">Objectif : -{{ challengeReduction }}kg</p>
      </div>

      <!-- Comparaison avec hier -->
      <div v-if="yesterdayWaste" class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3 text-center">📅 Par rapport à hier</h3>
        <div class="flex items-center justify-center gap-8">
          <div class="text-center">
            <p class="text-sm text-gray-600">Hier</p>
            <p class="text-3xl font-bold text-gray-500">{{ yesterdayWaste }} kg</p>
          </div>
          <div class="text-5xl">
            {{ totalWaste < yesterdayWaste ? "😊" : "😕" }}
          </div>
          <div class="text-center">
            <p class="text-sm text-gray-600">Aujourd'hui</p>
            <p class="text-3xl font-bold" :class="totalWaste < yesterdayWaste ? 'text-green-600' : 'text-red-600'">
              {{ totalWaste }} kg
            </p>
          </div>
        </div>
        <div class="text-center mt-4">
          <p v-if="totalWaste < yesterdayWaste" class="text-green-600 font-bold text-lg">
            👏 Bravo ! Vous avez réduit de {{ (yesterdayWaste - totalWaste).toFixed(1) }}kg !
          </p>
          <p v-else class="text-orange-600 font-semibold">On peut faire mieux demain !</p>
        </div>
      </div>

      <!-- Bouton retour -->
      <button
        @click="backToInput"
        class="w-full bg-gray-500 hover:bg-gray-600 text-white rounded-lg p-4 font-semibold shadow-md transition"
      >
        ← Retour à la saisie
      </button>
    </div>

    <!-- Mode affiche imprimable -->
    <div v-if="mode === 'poster'" class="bg-white p-8 space-y-6">
      <div class="text-center border-8 border-red-500 rounded-2xl p-6 bg-red-50">
        <h1 class="text-5xl font-bold text-red-600 mb-4">🗑️ GASPILLAGE</h1>
        <p class="text-3xl mb-2">Lundi 5 janvier</p>
        <p class="text-7xl font-bold text-red-700 my-6">{{ totalWaste }} KG</p>
        <p class="text-2xl">jetés à la poubelle</p>
      </div>

      <div class="grid grid-cols-2 gap-6">
        <div
          v-for="equiv in equivalences.slice(0, 2)"
          :key="equiv.id"
          class="border-4 border-blue-400 rounded-xl p-6 text-center bg-blue-50"
        >
          <p class="text-6xl mb-3">{{ equiv.icon }}</p>
          <p class="text-4xl font-bold text-blue-600">{{ equiv.quantity }}</p>
          <p class="text-xl mt-2">{{ equiv.label }}</p>
        </div>
      </div>

      <div class="bg-green-100 border-4 border-green-500 rounded-xl p-6 text-center">
        <p class="text-4xl mb-3">🎯</p>
        <p class="text-3xl font-bold text-green-700">NOTRE DÉFI</p>
        <p class="text-2xl mt-3">Essayons de faire mieux demain !</p>
      </div>

      <div class="flex gap-3 justify-center">
        <button @click="printPoster" class="bg-blue-500 text-white px-6 py-3 rounded-lg font-bold text-lg">
          🖨️ Imprimer
        </button>
        <button @click="backToInput" class="bg-gray-400 text-white px-6 py-3 rounded-lg font-bold text-lg">
          ← Retour
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WasteVisualization",
  data() {
    return {
      mode: "input", // 'input', 'display', 'poster'
      wasteCategories: [
        { id: 1, name: "Pain", icon: "🥖", barquette: null },
        { id: 2, name: "Légumes", icon: "🥗", barquette: null },
        { id: 3, name: "Viande/Poisson", icon: "🍖", barquette: null },
        { id: 4, name: "Féculents", icon: "🍚", barquette: null },
        { id: 5, name: "Desserts", icon: "🍮", barquette: null },
        { id: 6, name: "Fruits", icon: "🍎", barquette: null },
      ],
      barquetteOptions: {
        Pain: [
          { label: "0", value: 0 },
          { label: "1/4", value: 0.25 },
          { label: "1/2", value: 0.5 },
          { label: "1", value: 1 },
          { label: "2", value: 2 },
          { label: "4", value: 4 },
        ],
        Légumes: [
          { label: "0", value: 0 },
          { label: "1/4", value: 0.25 },
          { label: "1/2", value: 0.5 },
          { label: "1", value: 1 },
          { label: "2", value: 2 },
          { label: "4", value: 4 },
        ],
        "Viande/Poisson": [
          { label: "0", value: 0 },
          { label: "1/4", value: 0.25 },
          { label: "1/2", value: 0.5 },
          { label: "1", value: 1 },
          { label: "2", value: 2 },
          { label: "4", value: 4 },
        ],
        Féculents: [
          { label: "0", value: 0 },
          { label: "1/4", value: 0.25 },
          { label: "1/2", value: 0.5 },
          { label: "1", value: 1 },
          { label: "2", value: 2 },
          { label: "4", value: 4 },
        ],
        Desserts: [
          { label: "0", value: 0 },
          { label: "1/4", value: 0.25 },
          { label: "1/2", value: 0.5 },
          { label: "1", value: 1 },
          { label: "2", value: 2 },
          { label: "4", value: 4 },
        ],
        Fruits: [
          { label: "0", value: 0 },
          { label: "1/4", value: 0.25 },
          { label: "1/2", value: 0.5 },
          { label: "1", value: 1 },
          { label: "2", value: 2 },
          { label: "4", value: 4 },
        ],
      },
      photos: [],
      yesterdayWaste: 4.8,
      challengeReduction: 1.0,
    }
  },
  computed: {
    totalWaste() {
      // Calcule le total en kg selon les barquettes sélectionnées
      let total = 0
      for (const cat of this.wasteCategories) {
        total += this.optToKg(cat.name, cat.barquette)
      }
      return total.toFixed(2)
    },
    wasteLevel() {
      // Niveau de remplissage de 0 à 100%
      return Math.min((this.totalWaste / 10) * 100, 100)
    },
    equivalences() {
      const waste = parseFloat(this.totalWaste)
      return [
        {
          id: 1,
          icon: "🍔",
          quantity: Math.round(waste / 0.2),
          label: "hamburgers",
        },
        {
          id: 2,
          icon: "🍎",
          quantity: Math.round(waste / 0.15),
          label: "pommes",
        },
        {
          id: 3,
          icon: "🥖",
          quantity: Math.round(waste / 0.25),
          label: "baguettes",
        },
        {
          id: 4,
          icon: "💧",
          quantity: Math.round(waste * 1000),
          label: "litres d'eau gaspillés pour produire ces aliments",
        },
      ]
    },
  },
  methods: {
    setBarquette(cat, value) {
      cat.barquette = value
    },
    optToKg(name, value) {
      if (!value) return 0
      switch (name) {
        case "Pain":
          if (value === 0.25) return 0.05
          if (value === 0.5) return 0.1
          if (value === 1) return 0.25
          if (value === 2) return 0.5
          if (value === 4) return 1
          return 0
        case "Légumes":
        case "Féculents":
        case "Desserts":
        case "Fruits":
          if (value === 0.25) return 0.25
          if (value === 0.5) return 0.5
          if (value === 1) return 1
          if (value === 2) return 2
          if (value === 4) return 4
          return 0
        case "Viande/Poisson":
          if (value === 0.25) return 0.5
          if (value === 0.5) return 1
          if (value === 1) return 2
          if (value === 2) return 4
          if (value === 4) return 8
          return 0
        default:
          return 0
      }
    },
    addPhoto() {
      this.photos.push({ id: Date.now() })
      alert("Photo ajoutée")
    },
    removePhoto(idx) {
      this.photos.splice(idx, 1)
    },
    generateVisualization() {
      if (this.totalWaste > 0) {
        this.mode = "display"
      }
    },
    saveForTomorrow() {
      if (this.totalWaste > 0) {
        this.mode = "poster"
      }
    },
    backToInput() {
      this.mode = "input"
    },
    printPoster() {
      window.print()
    },
  },
}
</script>

<style scoped>
.space-y-4 > * + * {
  margin-top: 1rem;
}
.space-y-3 > * + * {
  margin-top: 0.75rem;
}
.space-y-2 > * + * {
  margin-top: 0.5rem;
}
.space-y-6 > * + * {
  margin-top: 1.5rem;
}
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 0.75rem;
}
.gap-4 {
  gap: 1rem;
}
.gap-6 {
  gap: 1.5rem;
}
.gap-8 {
  gap: 2rem;
}
.grid {
  display: grid;
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.flex {
  display: flex;
}
.flex-1 {
  flex: 1 1 0%;
}
.flex-wrap {
  flex-wrap: wrap;
}
.items-center {
  align-items: center;
}
.justify-center {
  justify-content: center;
}
.justify-between {
  justify-content: space-between;
}
.relative {
  position: relative;
}
.absolute {
  position: absolute;
}
.inline-block {
  display: inline-block;
}
.-top-2 {
  top: -0.5rem;
}
.-right-2 {
  right: -0.5rem;
}
.bottom-0 {
  bottom: 0;
}
.left-0 {
  left: 0;
}
.right-0 {
  right: 0;
}
.bg-gradient-to-br {
  background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
}
.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}
.from-green-500 {
  --tw-gradient-from: #22c55e;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
.to-green-600 {
  --tw-gradient-to: #16a34a;
}
.from-orange-500 {
  --tw-gradient-from: #f97316;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
.to-red-500 {
  --tw-gradient-to: #ef4444;
}
.from-blue-50 {
  --tw-gradient-from: #eff6ff;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
.to-purple-50 {
  --tw-gradient-to: #faf5ff;
}
.from-green-400 {
  --tw-gradient-from: #4ade80;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
.to-green-500 {
  --tw-gradient-to: #22c55e;
}
.rounded-xl {
  border-radius: 0.75rem;
}
.rounded-2xl {
  border-radius: 1rem;
}
.rounded-lg {
  border-radius: 0.5rem;
}
.rounded-full {
  border-radius: 9999px;
}
.rounded-b-3xl {
  border-bottom-left-radius: 1.5rem;
  border-bottom-right-radius: 1.5rem;
}
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}
.shadow-md {
  box-shadow: 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
.shadow-lg {
  box-shadow: 0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.border-2 {
  border-width: 2px;
}
.border-4 {
  border-width: 4px;
}
.border-8 {
  border-width: 8px;
}
.border-dashed {
  border-style: dashed;
}
.border-gray-200 {
  border-color: #e5e7eb;
}
.border-orange-300 {
  border-color: #fdba74;
}
.border-blue-200 {
  border-color: #bfdbfe;
}
.border-blue-300 {
  border-color: #93c5fd;
}
.border-blue-400 {
  border-color: #60a5fa;
}
.border-red-300 {
  border-color: #fca5a5;
}
.border-red-500 {
  border-color: #ef4444;
}
.border-green-500 {
  border-color: #22c55e;
}
.bg-white {
  background-color: #ffffff;
}
.bg-red-50 {
  background-color: #fef2f2;
}
.bg-red-500 {
  background-color: #ef4444;
}
.bg-orange-50 {
  background-color: #fff7ed;
}
.bg-blue-50 {
  background-color: #eff6ff;
}
.bg-blue-100 {
  background-color: #dbeafe;
}
.hover\:bg-blue-100:hover {
  background-color: #dbeafe;
}
.bg-blue-500 {
  background-color: #3b82f6;
}
.bg-blue-600 {
  background-color: #2563eb;
}
.hover\:bg-blue-600:hover {
  background-color: #2563eb;
}
.bg-green-100 {
  background-color: #dcfce7;
}
.bg-green-500 {
  background-color: #22c55e;
}
.bg-green-600 {
  background-color: #16a34a;
}
.hover\:bg-green-600:hover {
  background-color: #16a34a;
}
.bg-gray-100 {
  background-color: #f3f4f6;
}
.bg-gray-300 {
  background-color: #d1d5db;
}
.bg-gray-400 {
  background-color: #9ca3af;
}
.bg-gray-500 {
  background-color: #6b7280;
}
.bg-gray-600 {
  background-color: #4b5563;
}
.hover\:bg-gray-600:hover {
  background-color: #4b5563;
}
.text-white {
  color: #ffffff;
}
.text-blue-500 {
  color: #3b82f6;
}
.text-blue-600 {
  color: #2563eb;
}
.text-blue-700 {
  color: #1d4ed8;
}
.text-red-600 {
  color: #dc2626;
}
.text-red-700 {
  color: #b91c1c;
}
.text-green-600 {
  color: #16a34a;
}
.text-green-700 {
  color: #15803d;
}
.text-orange-600 {
  color: #ea580c;
}
.text-gray-400 {
  color: #9ca3af;
}
.text-gray-500 {
  color: #6b7280;
}
.text-gray-600 {
  color: #4b5563;
}
.text-gray-700 {
  color: #374151;
}
.text-gray-800 {
  color: #1f2937;
}
.font-bold {
  font-weight: 700;
}
.font-semibold {
  font-weight: 600;
}
.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}
.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}
.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem;
}
.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem;
}
.text-3xl {
  font-size: 1.875rem;
  line-height: 2.25rem;
}
.text-4xl {
  font-size: 2.25rem;
  line-height: 2.5rem;
}
.text-5xl {
  font-size: 3rem;
  line-height: 1;
}
.text-6xl {
  font-size: 3.75rem;
  line-height: 1;
}
.text-7xl {
  font-size: 4.5rem;
  line-height: 1;
}
.text-9xl {
  font-size: 8rem;
  line-height: 1;
}
.text-center {
  text-align: center;
}
.p-3 {
  padding: 0.75rem;
}
.p-4 {
  padding: 1rem;
}
.p-6 {
  padding: 1.5rem;
}
.p-8 {
  padding: 2rem;
}
.px-6 {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
.py-3 {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}
.mb-1 {
  margin-bottom: 0.25rem;
}
.mb-2 {
  margin-bottom: 0.5rem;
}
.mb-3 {
  margin-bottom: 0.75rem;
}
.mb-4 {
  margin-bottom: 1rem;
}
.mb-6 {
  margin-bottom: 1.5rem;
}
.mt-2 {
  margin-top: 0.5rem;
}
.mt-3 {
  margin-top: 0.75rem;
}
.mt-4 {
  margin-top: 1rem;
}
.my-6 {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
}
.w-full {
  width: 100%;
}
.w-6 {
  width: 1.5rem;
}
.w-24 {
  width: 6rem;
}
.h-6 {
  height: 1.5rem;
}
.h-24 {
  height: 6rem;
}
.opacity-70 {
  opacity: 0.7;
}
.opacity-90 {
  opacity: 0.9;
}
.cursor-not-allowed {
  cursor: not-allowed;
}
.transition {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
button {
  cursor: pointer;
  border: none;
  background: none;
}
button:not(:disabled):hover {
  opacity: 0.9;
}
input[type="range"] {
  width: 100%;
}

@media print {
  button {
    display: none;
  }
}
</style>
